import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MENU_CONFIG, type AppUser} from '../../data/menu';
import { UserRole } from '../../types/UserRole';

/**
 * ============================================================================
 * MenuPanel コンポーネント 
 * ============================================================================
 * ユーザーの権限に基づいて、操作可能なメニュー一覧を表示するパネル部品です。
 * * [主な機能]
 * 1. 権限(ロール)チェック: ユーザーが許可されていないメニューを非表示にします。
 * 2. 空カテゴリの除外: 表示できるメニューが1つもないカテゴリは隠します。
 * 3. デザイン: 横長のカード型ボタンを採用し、ホバー時に青く光る演出を行います。
 */

interface MenuPanelProps {
  currentUser: AppUser; // 親(MenuPage)から渡されるログインユーザー情報
}

export const MenuPanel = ({ currentUser }: MenuPanelProps) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // 1. 権限チェック関数
  // --------------------------------------------------------------------------
  // メニュー項目に設定された roles 配列に、自分のロールが含まれているか判定します。
  // 'all' が含まれていれば誰でもOKです。
  const hasPermission = (roles: (UserRole | 'all')[]) => {
    // データ不備への対策（念のため）
    if (!currentUser) return false;
    
    // ユーザーのロールを取得 (なければ 'buyer' 扱い)
    const userRole = currentUser.role || 'buyer';
    
    return roles.includes(userRole) || roles.includes('all');
  };

  // --------------------------------------------------------------------------
  // 2. 表示データの生成 (フィルタリング処理)
  // --------------------------------------------------------------------------
  // MENU_CONFIG 全体から、「自分に関係あるもの」だけを抽出した新しいリストを作ります。
  const availableCategories = MENU_CONFIG.filter(category => {
    // (A) 「ホーム」カテゴリはトップメニューに表示しないルールなので除外
    if (category.category === 'ホーム') return false;
    
    // (B) そのカテゴリの中に、権限があるアイテムが「1つでもあるか」確認
    const hasAnyItem = category.items.some(item => hasPermission(item.roles));
    
    // 1つもなければ、そのカテゴリごと非表示にする
    return hasAnyItem;
  });

  return (
    <div className="flex flex-col gap-8 w-full">
      {/* フィルタリング済みのカテゴリをループして表示 */}
      {availableCategories.map((category, index) => {
        
        // カテゴリ内のアイテムをさらにフィルタリング (権限がない個別のボタンを消す)
        const availableItems = category.items.filter(item => hasPermission(item.roles));

        // 万が一ここで空になれば何も表示しない
        if (availableItems.length === 0) return null;

        // アイコンコンポーネントを取り出す
        const Icon = category.icon;

        return (
          // --- カテゴリごとのセクション (白い大きな箱) ---
          <section 
            key={index} 
            className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden w-full"
          >
            
            {/* --- カテゴリヘッダー (アイコン + タイトル) --- */}
            <div className="px-6 py-4 border-b border-slate-100 bg-white flex items-center gap-3">
              <div className="p-2 bg-slate-50 text-slate-600 rounded-lg flex-shrink-0">
                <Icon size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-700 tracking-tight">
                {category.category}
              </h3>
            </div>
            
            {/* --- ボタン一覧エリア --- */}
            <div className="p-6 bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {availableItems.map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    onClick={() => navigate(item.path)}
                    className="
                      /* === ボタン全体のスタイル === */
                      group relative w-full flex items-center justify-between p-4 
                      bg-white border border-slate-200 rounded-xl
                      text-left 
                      
                      /* === ホバー時のアニメーション === */
                      hover:border-blue-500    /* 枠線を青く */
                      hover:shadow-md          /* 影をつけて浮かせる */
                      hover:bg-blue-50/10      /* 背景をほんのり青く */
                      hover:-translate-y-0.5   /* 1ピクセルだけ上に動く */
                      
                      transition-all duration-200 active:scale-[0.99]
                    "
                  >
                    {/* 左側: テキスト情報 */}
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="
                        font-bold text-slate-700 
                        group-hover:text-blue-700 /* 親(button)ホバー時に青文字にする */
                        mb-1 truncate text-base transition-colors
                      ">
                        {item.name}
                      </div>
                      <div className="text-xs text-slate-400 font-medium truncate">
                        機能画面へ移動
                      </div>
                    </div>
                    
                    {/* 右側: 矢印アイコン */}
                    <div className="
                      w-8 h-8 rounded-full 
                      bg-slate-100 text-slate-400
                      border border-transparent
                      
                      /* ホバー時に青背景・白文字にする */
                      group-hover:bg-blue-600 
                      group-hover:text-white 
                      
                      flex items-center justify-center flex-shrink-0 
                      transition-all
                    ">
                      {/* 矢印自体も少し右に動くアニメーション */}
                      <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </button>
                ))}

              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};